import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/app/ui-bits";
import {
  UsersThree, Warning, Star, CalendarCheck,
  SquaresFour, FileText, Coins, Clock, Briefcase,
  SignOut, Files, UserPlus, UserMinus, ShieldCheck, ClipboardText,
} from "@phosphor-icons/react";
import { useAuth, hasMinRole } from "@/contexts/AuthContext";
import useNewRecordHighlight from "@/hooks/useNewRecordHighlight";
import ExportMenu from "@/components/app/ExportMenu";
import { SimpleList } from "@/components/hr/_shared";
import HRDashboard from "@/components/hr/HRDashboard";
import StaffDirectory from "@/components/hr/StaffDirectory";
import Contracts from "@/components/hr/Contracts";
import SalaryRequests from "@/components/hr/SalaryRequests";
import PayrollReceipts from "@/components/hr/PayrollReceipts";
import AttendanceLogs from "@/components/hr/AttendanceLogs";
import Warnings from "@/components/hr/Warnings";
import Defenses from "@/components/hr/Defenses";
import Leaves from "@/components/hr/Leaves";
import Separations from "@/components/hr/Separations";
import PastEmployees from "@/components/hr/PastEmployees";
import OnboardingGuide from "@/components/hr/Onboarding";
import PositionControl from "@/components/hr/PositionControl";
import DocumentsGuide from "@/components/hr/DocumentsGuide";

const TABS = [
  { id: "dashboard", label: "HR Dashboard", icon: SquaresFour },
  { id: "directory", label: "Staff Directory", icon: UsersThree },
  { id: "contracts", label: "Contracts", icon: FileText },
  { id: "salary", label: "Salary Requests", icon: Coins },
  { id: "receipts", label: "Payroll Receipts", icon: ClipboardText },
  { id: "attendance", label: "Attendance", icon: Clock },
  { id: "performance", label: "Performance", icon: Star },
  { id: "warnings", label: "Warnings & Discipline", icon: Warning },
  { id: "defense", label: "Employee Defense", icon: ShieldCheck },
  { id: "leaves", label: "Leave & Absence", icon: CalendarCheck },
  { id: "separation", label: "Separations", icon: SignOut },
  { id: "past", label: "Past Employees", icon: UserMinus },
  { id: "onboarding", label: "Onboarding / Offboarding", icon: UserPlus },
  { id: "vacancy", label: "Position Control", icon: Briefcase },
  { id: "documents", label: "Documents", icon: Files },
];

export default function HR() {
  const { user } = useAuth();
  const isAdmin = hasMinRole(user, "admin");
  const [tab, setTab] = useState("dashboard");
  const [staff, setStaff] = useState([]);
  const [loadingStaff, setLoadingStaff] = useState(true);
  const { newId, highlightRef, classForId } = useNewRecordHighlight(loadingStaff);

  // If arriving with ?new=<id>, jump straight to the Staff Directory tab
  useEffect(() => { if (newId) setTab("directory"); }, [newId]);

  const loadStaff = async () => {
    setLoadingStaff(true);
    try { setStaff(await (await api.get("/hr/staff")).data); } finally { setLoadingStaff(false); }
  };
  useEffect(() => { loadStaff(); }, []);

  return (
    <div data-testid="hr-page">
      <PageHeader
        title="Human Resources"
        description="Full employee lifecycle — from onboarding to contracts, payroll, performance, discipline, and separation."
        actions={
          <div className="flex items-center gap-2">
            <ExportMenu module="hr" />
          </div>
        }
      />

      <div className="p-5 md:p-8 space-y-4">
        <div className="relative rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
          <div className="flex gap-1 overflow-x-auto pr-1" style={{ scrollbarWidth: "thin", WebkitOverflowScrolling: "touch" }}
               data-testid="hr-tabs-scroller">
            {TABS.map(({ id, label, icon: Icon }) => {
              const isActive = tab === id;
              return (
                <button
                  key={id}
                  onClick={() => setTab(id)}
                  className={`inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-lg px-3.5 py-2 text-xs font-medium transition ${
                    isActive ? "bg-[#0f1e42] text-white" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  }`}
                  data-testid={`hr-tab-${id}`}
                >
                  <Icon size={14} /> <span>{label}</span>
                </button>
              );
            })}
          </div>
          <div className="pointer-events-none absolute inset-y-1 right-1 hidden w-10 rounded-r-xl bg-gradient-to-l from-white to-transparent md:block" />
        </div>

        {tab === "dashboard" && <HRDashboard staff={staff} />}
        {tab === "directory" && <StaffDirectory staff={staff} reload={loadStaff} loading={loadingStaff} newId={newId} highlightRef={highlightRef} classForId={classForId} />}
        {tab === "contracts" && <Contracts staff={staff} />}
        {tab === "salary" && <SalaryRequests staff={staff} />}
        {tab === "receipts" && <PayrollReceipts staff={staff} />}
        {tab === "attendance" && <AttendanceLogs staff={staff} />}
        {tab === "performance" && <SimpleList title="Performance Reviews" note="Performance reviews are embedded per staff member. Use Staff Directory → staff profile → Performance section. Each profile now shows a 7-tile weighted breakdown (Task · Effort · Attendance · Discipline · Contract) including timesheet effort." />}
        {tab === "warnings" && <Warnings staff={staff} />}
        {tab === "defense" && <Defenses staff={staff} />}
        {tab === "leaves" && <Leaves staff={staff} />}
        {tab === "separation" && <Separations staff={staff} />}
        {tab === "past" && <PastEmployees />}
        {tab === "onboarding" && <OnboardingGuide staff={staff} />}
        {tab === "vacancy" && <PositionControl staff={staff} />}
        {tab === "documents" && <DocumentsGuide />}
      </div>
    </div>
  );
}
